import { useEffect, useState } from 'react';
import { Icon } from '@/components/primitives';
import { getSeedMeta, type SeedMeta } from '@/lib/seed-loader';
import { mockStore } from '@/lib/mock-store';

interface Props {
  /** Optional extra subtitle line below the banner (e.g. counts) */
  subtitle?: string;
}

/**
 * Inline banner showing the data calibration source. Reads metadata persisted
 * by seed-loader after the BQ-calibrated snapshot is fetched. Renders nothing
 * if there is no calibration info (e.g. running against real Supabase).
 */
export function CalibrationBanner({ subtitle }: Props) {
  const [meta, setMeta] = useState<SeedMeta | null>(null);

  useEffect(() => {
    setMeta(getSeedMeta());
  }, []);

  if (!meta || !meta.calibration_source) return null;

  const window = meta.calibration_window
    ? `${meta.calibration_window.start} → ${meta.calibration_window.end}`
    : '';
  const generated = meta.generated_at ? meta.generated_at.slice(0, 10) : '';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 14px',
        marginBottom: 16,
        background: 'linear-gradient(90deg, #eef2ff 0%, #f5f3ff 100%)',
        border: '1px solid #c7d2fe',
        borderRadius: 10,
        fontSize: 12.5,
        color: '#1e3a8a',
        lineHeight: 1.5,
      }}
      data-testid="calibration-banner"
    >
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: 8,
          background: '#0b43ea',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon name="database" size={16} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, color: '#1e3a8a' }}>
          📊 Calibrated from BigQuery actuals
        </div>
        <div style={{ color: '#3730a3', marginTop: 2 }}>
          Source: <code style={{ fontSize: 11.5 }}>{meta.calibration_source}</code>
          {window && ` · Window ${window}`}
          {generated && ` · Generated ${generated}`}
        </div>
        {subtitle && (
          <div style={{ color: '#6366f1', marginTop: 2, fontSize: 12 }}>{subtitle}</div>
        )}
      </div>
    </div>
  );
}

/**
 * Convenience subtitle producer — counts entities in the current mock store.
 */
export function useDataSnapshotSummary(): string {
  const [summary, setSummary] = useState('');
  useEffect(() => {
    const wh = mockStore.getAll('warehouses').length;
    const cu = mockStore.getAll('customers').length;
    const cfg = mockStore.getAll('config_values').length;
    const hist = mockStore.getAll('historical_orders').length;
    setSummary(
      `${wh} warehouses · ${cu} customers · ${cfg.toLocaleString()} configs · ${hist.toLocaleString()} historical orders`,
    );
  }, []);
  return summary;
}
